import { getItem } from 'api/utils/storageManager'
import PropertyCardSkeleton from 'components/propertyCard/PropertyCardSkeleton'
import { useCurrentIndex } from 'components/utils'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ViewMode } from 'types/customParams'
import { ListedProperty } from 'types/property'

import PropertyCard from '../propertyCard/PropertyCard'
import ListItem from './ListItem'

interface PropertyListProps {
  properties: ListedProperty[]
  loadingFavoriteId?: string | null
  handleFavorite?: (propertyId: string) => Promise<void>
  path?: string
  returnTo?: string
  isLoading?: boolean
}

const PropertyList = ({
  properties,
  loadingFavoriteId,
  handleFavorite,
  path = 'listing',
  returnTo,
  isLoading = false,
}: PropertyListProps) => {
  const [isGrid, setIsGrid] = useState(true)
  const [hasMountedGrid, setHasMountedGrid] = useState(true)
  const [hasMountedList, setHasMountedList] = useState(false)
  const navigate = useNavigate()
  const { currentIndex, setCurrentIndex } = useCurrentIndex()

  useEffect(() => {
    const storedView = getItem(ViewMode.ViewModeKey) as ViewMode | null
    const grid = storedView !== 'list'
    setIsGrid(grid)
    setHasMountedGrid(grid)
    setHasMountedList(!grid)
  }, [])

  useEffect(() => {
    const handleViewModeChange = () => {
      const storedView = getItem(ViewMode.ViewModeKey) as ViewMode | null
      const grid = storedView !== 'list'
      setIsGrid(grid)
      if (grid) {
        setHasMountedGrid(true)
      } else {
        setHasMountedList(true)
      }
    }

    window.addEventListener('viewModeChanged', handleViewModeChange)
    return () =>
      window.removeEventListener('viewModeChanged', handleViewModeChange)
  }, [])

  const gridClassName =
    'grid w-full gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
  const listClassName = 'flex flex-col gap-2 w-full'

  const openListing = (propertyId: string) => {
    navigate(`/${path}/${propertyId}`, {
      ...(returnTo ? { state: { returnTo } } : {}),
    })
  }

  if (isLoading) {
    return (
      <div className="w-full min-w-0" aria-busy="true" aria-live="polite">
        <div className={isGrid ? gridClassName : listClassName}>
          {Array.from({ length: 6 }).map((_, index) => (
            <PropertyCardSkeleton
              key={`skeleton-${index}`}
              variant={isGrid ? 'grid' : 'list'}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full min-w-0">
      {hasMountedGrid ? (
        <div className={isGrid ? gridClassName : 'hidden'} aria-hidden={!isGrid}>
          {Array.isArray(properties) &&
            properties.map((property, index) => (
              <PropertyCard
                key={property.id}
                handleClick={() => openListing(property.id)}
                property={property as any}
                currentIndex={currentIndex}
                listIndex={index}
                loadingFavoriteId={loadingFavoriteId}
                className="w-full min-w-0 relative"
                onMouseEnter={() => setCurrentIndex(index)}
                onMouseLeave={() => setCurrentIndex(0)}
                handleFavorite={handleFavorite}
              />
            ))}
        </div>
      ) : null}

      {hasMountedList ? (
        <div
          className={!isGrid ? listClassName : 'hidden'}
          aria-hidden={isGrid}
        >
          {Array.isArray(properties) &&
            properties.map(property => (
              <ListItem
                key={property.id}
                handleClick={() => openListing(property.id)}
                property={property as any}
                currentIndex={currentIndex}
                loadingFavoriteId={loadingFavoriteId}
                className="w-full min-w-0 relative"
                handleFavorite={handleFavorite}
              />
            ))}
        </div>
      ) : null}
    </div>
  )
}

export default PropertyList
